import { useForm } from "react-hook-form";
import { useUserContext } from "../context/UserProvider";
import { endpoints } from "../../../services/apiService/endpoint";
import {
  getJsonHelper,
  postJsonHelperWithToken,
} from "../../../services/apiService/apiHelper";
import { toast } from "react-toastify";

export const useUserHook = () => {
  const {
    currentStep,
    setCurrentStep,
    formValues,
    setFormValues,
    pincodeData,
    setPincodeData,
    loading,
    setLoading,
    selectedPincode,
    setSelectedPincode,
    city,
    setCity,
    state,
    setState,
    searchQuery,
    setSearchQuery,
    totalPageCount,
    setTotalPageCount,
    currentPage,
    setCurrentPage,
    allusersArr,
    setAllUsersArr,
    isNextPage,
    setIsNextPage,
    isPrevPage,
    setIsPrevPage,
    selectedUser,
    setSelectedUser,
  } = useUserContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues: formValues,
    mode: "onBlur",
  });

  const steps = [
    { id: 1, label: "User information" },
    { id: 2, label: "Additional Details" },
    { id: 3, label: "Review" },
  ];

  const handleNextButton = () => {
    if (!Object.keys(errors).length && currentStep !== 3) {
      handleSubmit(stepFormData)();
      setCurrentStep((prevStep) => prevStep + 1);
    }
    if (currentStep === 3) {
      handleCreateUser();
    }
  };

  const handleBackButton = () => {
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 1));
  };

  const resetStepsFromStart = () => {
    setCurrentStep(1);
  };

  const stepFormData = (data) => {
    setFormValues({ ...formValues, ...data });
  };

  const departmentArr = [
    { value: "HR", label: "HR" },
    { value: "IT", label: "IT" },
    { value: "Finance", label: "Finance" },
    { value: "Admin", label: "Admin" },
    { value: "Sales", label: "Sales" },
  ];

  const designationArr = [
    { value: "Manager", label: "Manager" },
    { value: "Lead", label: "Lead" },
    { value: "Senior Developer", label: "Senior Developer" },
    { value: "Junior Developer", label: "Junior Developer" },
    { value: "Intern", label: "Intern" },
    { value: "CEO", label: "CEO" },
    { value: "CFO", label: "CFO" },
    { value: "CTO", label: "CTO" },
    { value: "COO", label: "COO" },
    { value: "CIO", label: "CIO" },
  ];

  const rolesArr = [
    { value: "admin", label: "Admin" },
    { value: "Employee", label: "Employee" },
  ];

  const fetchPincodeData = async (pincode) => {
    if (pincode.length < 3) {
      setPincodeData([]);
      return;
    }
    setLoading(true);
    try {
      const response = await getJsonHelper(endpoints.pincode, pincode);

      if (response.data.status === true) {
        setPincodeData(response.data.data);
      } else {
        console.log(response.data.message, "error in fetching pincode data");
        setPincodeData([]);
      }
    } catch (err) {
      console.log(err, "error in fetching pincode data");
      setPincodeData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchQuery = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchPincodeData(value);
  };

  const handlePincodeSelect = async (pincode) => {
    try {
      const response = await getJsonHelper(endpoints.pincode, pincode);
      if (response.data.status === true) {
        setSelectedPincode(pincode);
        setCity(response.data.data[0].city);
        setState(response.data.data[0].state);

        setValue("city", response.data.data[0].city);
        setValue("state", response.data.data[0].state);
        setValue("pincode", pincode);
        setSearchQuery(`${pincode}`);
        setPincodeData([]);
      } else {
        console.log(response.data.message, "error in fetching pincode data");
      }
    } catch (err) {
      console.log(err, "error in fetching pincode data");
    }
  };

  const resetForm = () => {
    setFormValues({
      first_name: "",
      last_name: "",
      gender: "",
      birth_date: "",
      email: "",
      password: "",
      phone: "",
      designation: "",
      department: "",
      role: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      pincode: "",
    });
  };

  const handleCreateUser = async () => {
    try {
      const response = await postJsonHelperWithToken(
        endpoints.createUser,
        formValues
      );

      if (response.data.status === true) {
        resetStepsFromStart();
        setCity("");
        setState("");
        setSelectedPincode(null);
        reset();
        resetForm();
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.log(err, "error in creating user");
    }
  };

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const response = await getJsonHelper(
        `${endpoints.allUsers}?page=${currentPage}&limit=5`
      );

      if (response.data.status === true) {
        setAllUsersArr(response.data.data);
        setTotalPageCount(response.data.totalPage);

        // Correctly set the navigation states
        setIsNextPage(currentPage < response.data.totalPage);
        setIsPrevPage(currentPage > 1);
      }
    } catch (err) {
      console.log(err, "error in fetching user data");
    } finally {
      setLoading(false);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPageCount) {
      setCurrentPage((prevPage) => prevPage + 1);
      fetchUserData();
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
      fetchUserData();
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPageCount) {
      setCurrentPage(page);
      fetchUserData();
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    watch,
    currentStep,
    steps,
    handleNextButton,
    handleBackButton,
    setCurrentStep,
    formValues,
    setFormValues,
    stepFormData,
    departmentArr,
    designationArr,
    rolesArr,
    resetStepsFromStart,
    fetchPincodeData,
    pincodeData,
    setPincodeData,
    loading,
    setLoading,
    selectedPincode,
    setSelectedPincode,
    handlePincodeSelect,
    city,
    setCity,
    state,
    setState,
    searchQuery,
    setSearchQuery,
    handleSearchQuery,
    handleCreateUser,
    totalPageCount,
    setTotalPageCount,
    currentPage,
    setCurrentPage,
    allusersArr,
    setAllUsersArr,
    isNextPage,
    setIsNextPage,
    isPrevPage,
    setIsPrevPage,
    fetchUserData,
    goToNextPage,
    goToPrevPage,
    goToPage,
    selectedUser,
    setSelectedUser,
  };
};
